import React, { useState } from 'react'

import { useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Button, TextInput } from '@atoms'
import { SelectField } from '@components/molecules/SelectField'
import { scopedTranslation, i18nValidation } from '@utils/I18n'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import useMutation from '@hooks/useGQLMutation'
import { AddGroupLeadersDocument } from '@frontend/graphql/mutations/addGroupLeaders.generated'
import * as SuccessModal from '@molecules/SuccessModal'
import { useErrorsStore } from '@stores/errorsStore'

const t = scopedTranslation('group_edit.manage_leaders.add_leaders')
const tAttributes = scopedTranslation('attributes')

type Leader = {
  email: string
  role: string
}

type InvitedUser = {
  id: string
  email: string
  fullName?: string | null
  role: string
}

type SuccessfullyInvitedLeaders = InvitedUser[]

export type AddLeaderFormValues = {
  leadersToAdd: Leader[]
}

export const validationSchema = yup.object().shape({
  leadersToAdd: yup.array().of(
    yup.object().shape({
      email: yup
        .string()
        .label(tAttributes('user.email'))
        .email(i18nValidation('valid'))
        .required(i18nValidation('required')),
      role: yup.string().label('Role').required('Role is required'),
    })
  ),
})

type AddLeadersFormProps = {
  groupID: string
  onLeadersChanged: (visit: { onFinish: () => void }) => void
}

export default function AddLeadersForm({ groupID, onLeadersChanged }: AddLeadersFormProps) {
  const roleOptions = ['captain', 'vice-captain']
  const defaultFieldValues = { email: '', role: roleOptions[0] }
  const [SuccessModalOpen, setSuccessModalOpen] = React.useState(false)
  const [leadersInvited, setLeadersInvited] = useState<SuccessfullyInvitedLeaders>([])
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: { leadersToAdd: [] },
  })

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'leadersToAdd',
  })

  const [reloading, setReloading] = useState(false)
  const { mutate: addGroupLeadersMutate, isLoading } = useMutation(AddGroupLeadersDocument)
  const busy = isLoading || reloading
  const { setErrors: setAppErrors, clearErrors: clearAppErrors } = useErrorsStore(({ setErrors, clearErrors }) => ({
    setErrors,
    clearErrors,
  }))

  const addGroupLeaders = (users) => {
    clearAppErrors('manageLeaders')
    addGroupLeadersMutate(
      { id: groupID, users },
      {
        onSuccess: (response) => {
          const successfullyInvitedLeaders = (response?.addGroupLeaders?.users || []).map((user) => ({
            id: user.id,
            email: user.email,
            fullName: user.fullName,
            role: user.userRoles.nodes![0]!.role.name!,
          }))

          setReloading(true)
          onLeadersChanged({ onFinish: () => setReloading(false) })
          setLeadersInvited(successfullyInvitedLeaders)
          setSuccessModalOpen(true)
          replace([])
        },
        onError: () => {
          setAppErrors({
            errorsPath: 'manageLeaders',
            errors: [
              {
                message: '',
                code: 'unknown',
              },
            ],
          })
        },
      }
    )
  }

  const onSubmit = async (values: yup.InferType<typeof validationSchema>) => {
    addGroupLeaders(values.leadersToAdd)
  }

  return (
    <div className="tw:flex tw:flex-col">
      <form onSubmit={handleSubmit(onSubmit)} className="tw:flex tw:flex-col tw:gap-4">
        <h3 className="tw:text-gray-900 tw:font-bold tw:text-3xl">{t('title')}</h3>
        <p>{t('description')}</p>

        {fields.map((leader, index) => (
          <div
            key={leader.id}
            className="tw:flex tw:md:flex-row tw:flex-col tw:xl:max-w-3xl tw:content-center tw:items-start tw:gap-4"
          >
            <div className="tw:w-full tw:md:min-w-[20ch]">
              <TextInput
                leadingIcon="mail-01"
                displayName={t('labels.email')}
                placeholder="email@example.com"
                errors={errors}
                {...register(`leadersToAdd.${index}.email`)}
              />
            </div>

            <div className="tw:h-fit tw:w-full tw:md:min-w-[180px] tw:md:w-fit">
              <SelectField
                name={`leadersToAdd.${index}.role`}
                label={t('labels.role')}
                options={roleOptions.map((roleName) => ({ value: roleName, label: roleName }))}
                value={watch(`leadersToAdd.${index}.role`)}
                onValueChange={(value) => setValue(`leadersToAdd.${index}.role`, value)}
              />
            </div>
            <div className="tw:ml-auto tw:md:ml-0 tw:md:mt-7">
              <Button
                type="button"
                ariaLabel={t('aria_labels.remove', { email: watch(`leadersToAdd.${index}.email`) })}
                size="md"
                leadingIcon="trash-01"
                rank="primary"
                color="danger"
                onClick={() => {
                  remove(index)
                }}
              >
                {t('labels.remove')}
              </Button>
            </div>
          </div>
        ))}

        <div>
          <Button
            type="button"
            size="md"
            leadingIcon="plus"
            rank="link"
            onClick={() => {
              append(defaultFieldValues)
            }}
          >
            {t('labels.add_new_leader')}
          </Button>
        </div>

        <div>
          <Button type="submit" size="md" trailingIcon="arrow-circle-broken-right" disabled={busy}>
            {t('labels.submit')}
          </Button>
        </div>
      </form>
      <SuccessModal.Root open={SuccessModalOpen} setOpen={setSuccessModalOpen}>
        <SuccessModal.Header>{t('dialog.title')}</SuccessModal.Header>
        <ul className="tw:list-disc tw:flex tw:flex-col tw:items-center">
          {leadersInvited?.map((user) => {
            return <li key={user.id}>{user.fullName || user.email}</li>
          })}
        </ul>
      </SuccessModal.Root>
    </div>
  )
}
